/**
 * Brazil EOL Interpreter VersionSets container: shows versionsets grouped
 * by runtime with pipeline selection checkboxes.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import React, { useMemo } from 'react';
import {
  Badge,
  Box,
  Checkbox,
  Container,
  ExpandableSection,
  Header,
  Icon,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Table
} from '@amzn/awsui-components-core';
import { BrazilEOLInterpreterData, VersionSetDetail } from '../types';
import { DebouncedSearchFilter } from '../components/DebouncedSearchFilter';

interface BrazilEOLContainerProps {
  brazilEOLData: BrazilEOLInterpreterData | null;
  isBrazilEOLLoading: boolean;
  brazilEOLError: string | null;
  brazilEOLRuntimeFilter: 'cpython' | 'nodejs';
  setBrazilEOLRuntimeFilter: (filter: 'cpython' | 'nodejs') => void;
  brazilEOLSearchQuery: string;
  handleBrazilEOLSearchFilterChange: (text: string) => void;
  selectedBrazilPipelines: Set<string>;
  toggleBrazilPipelineSelection: (pipelineName: string, e: React.MouseEvent) => void;
  isBrazilPipelineSelected: (pipelineName: string) => boolean;
}

export const BrazilEOLContainer: React.FC<BrazilEOLContainerProps> = ({
  brazilEOLData,
  isBrazilEOLLoading,
  brazilEOLError,
  brazilEOLRuntimeFilter,
  setBrazilEOLRuntimeFilter,
  brazilEOLSearchQuery,
  handleBrazilEOLSearchFilterChange,
  selectedBrazilPipelines,
  toggleBrazilPipelineSelection,
  isBrazilPipelineSelected
}) => {
  const runtimeGroups = brazilEOLRuntimeFilter === 'cpython' ? brazilEOLData?.cpythonGroups || [] : brazilEOLData?.nodejsGroups || [];

  const filteredGroups = useMemo(() => {
    const query = brazilEOLSearchQuery.toLowerCase().trim();
    if (!query) return runtimeGroups;

    return runtimeGroups
      .map((group) => ({
        ...group,
        pipelines: group.pipelines
          .map((p) => ({
            ...p,
            versionsets: p.versionsets.filter(
              (vs) =>
                vs.versionSet.toLowerCase().includes(query) ||
                p.name.toLowerCase().includes(query) ||
                (vs.cpythonRuntime && vs.cpythonRuntime.toLowerCase().includes(query)) ||
                (vs.nodejsRuntime && vs.nodejsRuntime.toLowerCase().includes(query))
            )
          }))
          .filter((p) => p.versionsets.length > 0)
      }))
      .map((group) => ({
        ...group,
        totalCount: group.pipelines.reduce((sum, p) => sum + p.versionsets.length, 0)
      }))
      .filter((group) => group.pipelines.length > 0);
  }, [runtimeGroups, brazilEOLSearchQuery]);

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="VersionSets running on older Brazil interpreter versions"
          actions={
            <SpaceBetween direction="horizontal" size="m" alignItems="center">
              <DebouncedSearchFilter onFilterChange={handleBrazilEOLSearchFilterChange} placeholder="Filter pipelines or versionsets" />
              <SegmentedControl
                selectedId={brazilEOLRuntimeFilter}
                onChange={({ detail }) => setBrazilEOLRuntimeFilter(detail.selectedId as 'cpython' | 'nodejs')}
                options={[
                  { id: 'cpython', text: `CPython (${brazilEOLData?.cpythonTotalCount || 0})` },
                  { id: 'nodejs', text: `NodeJS (${brazilEOLData?.nodejsTotalCount || 0})` }
                ]}
              />
            </SpaceBetween>
          }
        >
          Brazil EOL Interpreter VersionSets
        </Header>
      }
    >
      {isBrazilEOLLoading ? (
        <Box textAlign="center" padding="xl">
          <StatusIndicator type="loading">Loading VersionSet data...</StatusIndicator>
        </Box>
      ) : brazilEOLError ? (
        <Box textAlign="center" padding="xl">
          <StatusIndicator type="error">{brazilEOLError}</StatusIndicator>
        </Box>
      ) : filteredGroups.length === 0 ? (
        <Box textAlign="center" padding="xxl" color="text-body-secondary">
          {brazilEOLSearchQuery
            ? `No versionsets found matching "${brazilEOLSearchQuery}"`
            : `No versionsets found with EOL ${brazilEOLRuntimeFilter === 'cpython' ? 'CPython' : 'NodeJS'} runtimes`}
        </Box>
      ) : (
        <SpaceBetween size="m">
          {filteredGroups.map((group) => (
            <ExpandableSection
              key={group.runtime}
              variant="container"
              defaultExpanded={false}
              header={
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  {group.isEOL ? (
                    <Box fontWeight="bold" color="text-status-warning">
                      ⚠️ {group.runtime}
                    </Box>
                  ) : (
                    <Box fontWeight="bold">{group.runtime}</Box>
                  )}
                  <Badge color={group.isEOL ? 'red' : 'grey'}>{group.totalCount} versionsets</Badge>
                  <Badge color="grey">{group.pipelines.length} pipelines</Badge>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="s">
                {group.pipelines.map((pipelineGroup) => (
                  <ExpandableSection
                    key={`${group.runtime}-${pipelineGroup.name}`}
                    variant="default"
                    defaultExpanded={false}
                    header={
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <div onClick={(e) => toggleBrazilPipelineSelection(pipelineGroup.name, e)} style={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox checked={isBrazilPipelineSelected(pipelineGroup.name)} onChange={() => {}} />
                        </div>
                        <Icon name="share" variant="link" />
                        <span style={{ fontWeight: 500 }}>{pipelineGroup.name}</span>
                        <Badge color="blue">{pipelineGroup.versionsets.length} versionsets</Badge>
                      </SpaceBetween>
                    }
                  >
                    <Table
                      items={pipelineGroup.versionsets}
                      columnDefinitions={buildVersionSetColumns(brazilEOLRuntimeFilter)}
                      variant="embedded"
                      stripedRows
                    />
                  </ExpandableSection>
                ))}
              </SpaceBetween>
            </ExpandableSection>
          ))}
        </SpaceBetween>
      )}
    </Container>
  );
};

/** Build column definitions for the versionset table based on runtime filter. */
function buildVersionSetColumns(runtimeFilter: 'cpython' | 'nodejs') {
  const columns: any[] = [
    {
      id: 'versionSet',
      header: 'VersionSet',
      cell: (item: VersionSetDetail) => <Box fontWeight="bold">{item.versionSet}</Box>
    }
  ];

  if (runtimeFilter === 'cpython') {
    columns.push({
      id: 'cpython',
      header: 'Python Interpreter',
      cell: (item: VersionSetDetail) => {
        const cpythonEOLPatterns = [/cpython38/i, /cpython39/i];
        const isEOL = item.cpythonRuntime && cpythonEOLPatterns.some((p) => p.test(item.cpythonRuntime));
        return (
          <Box color={isEOL ? 'text-status-warning' : undefined}>
            {isEOL ? '⚠️ ' : ''}
            {item.cpythonRuntime}
          </Box>
        );
      }
    });
  }

  if (runtimeFilter === 'nodejs') {
    columns.push({
      id: 'nodejs',
      header: 'Node Interpreter',
      cell: (item: VersionSetDetail) => {
        const nodejsEOLPatterns = [/nodejs-16/i, /nodejs-18/i, /nodejs16/i, /nodejs18/i];
        const isEOL = item.nodejsRuntime && nodejsEOLPatterns.some((p) => p.test(item.nodejsRuntime));
        return (
          <Box color={isEOL ? 'text-status-warning' : undefined}>
            {isEOL ? '⚠️ ' : ''}
            {item.nodejsRuntime}
          </Box>
        );
      }
    });
  }

  return columns;
}
